import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Motion";
import { fetchCaseStudies } from "../lib/api";
import CTASection from "../components/CTASection";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies()
      .then((data) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="portfolio-page" className="pt-32">
      {/* HERO */}
      <section className="px-6 md:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Case Studies</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mt-6 max-w-5xl">
              Work that <em className="text-[#D4AF37] not-italic">compounds</em>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
              Selected engagements across healthcare, real estate, eCommerce,
              SaaS, and professional services.
            </p>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="px-6 md:px-8 py-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-white/40 text-sm" data-testid="portfolio-loading">
              Loading case studies...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
              {items.map((item, i) => (
                <Reveal key={item.id} delay={i * 0.06}>
                  <a
                    href="#"
                    data-testid={`case-${item.id}`}
                    className="block bg-[#0B0B0B] hover:bg-[#141414] transition-colors group h-full"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.client}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/30 to-transparent" />
                      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37]">
                            {item.industry}
                          </div>
                          <div className="font-display text-xl mt-1">
                            {item.client}
                          </div>
                        </div>
                        <ArrowUpRight
                          size={22}
                          className="text-white/60 group-hover:text-[#D4AF37] group-hover:rotate-45 transition-all duration-500"
                        />
                      </div>
                    </div>
                    <div className="p-8">
                      <h3 className="font-display text-xl md:text-[22px] leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-white/50 text-sm mt-3 leading-relaxed">
                        {item.summary}
                      </p>
                      <div className="mt-8 pt-6 border-t border-white/5 flex items-end justify-between">
                        <div>
                          <div className="font-display text-3xl text-[#D4AF37] tracking-tight">
                            {item.metric}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">
                            {item.metric_label}
                          </div>
                        </div>
                        <span className="text-xs text-white/40 uppercase tracking-[0.18em]">
                          Read case →
                        </span>
                      </div>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection testId="portfolio-cta" />
    </main>
  );
}
